<script setup lang="ts">
import { ref,computed, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDashboardStore } from '@/stores/dashboard'
import { ArrowDown } from '@element-plus/icons-vue'
import FeedbackDialog from './FeedbackDialog.vue'
import image1 from '@/assets/问题反馈.svg'
import image2 from '@/assets/点击问题反馈.svg'
import { getUpcomingHolidays } from '@/utils/holidayReminder'
import { ElMessage } from 'element-plus'
import { getAccountHistoryTypeLabel } from '@/views/PublicopinionContent/components/accountHistory'

interface BreadcrumbItem {
  text: string
  to?: string
}

const route = useRoute()
const dashboardStore = useDashboardStore()
const isDashboardPage = computed(() => route.name === 'PublicopinionDashboard')

// 页面配置：定义每个页面的基本信息和层级关系
interface PageConfig {
  name: string
  category: 'monitor' | 'disposal' | 'user' | 'media' | 'accountability' | 'budget'
  parent?: string
  parentRoute?: string
}

const pageConfigs: Record<string, PageConfig> = {
  PublicopinionDashboard: { name: '舆情看板', category: 'monitor' },
  PublicopinionDetail: { name: '舆情详情', category: 'monitor' },
  PublicopinionContent: {
    name: '舆情内容',
    category: 'monitor',
    parent: 'PublicopinionDetail',
    parentRoute: '/web/public-opinion-detail',
  },
  AccountPostList: {
    name: '账号帖子',
    category: 'monitor',
    parent: 'PublicopinionContent',
    parentRoute: '/web/public-opinion-content',
  },
  ArchiveDashboard: { name: '归档看板', category: 'disposal' },
  ArchiveDetail: {
    name: '归档详情',
    category: 'disposal',
    parent: 'ArchiveDashboard',
    parentRoute: '/web/archive-dashboard',
  },
  ProcessMonitor: { name: '流程监听', category: 'disposal' },
  InternalDetails: { name: '内部详情', category: 'disposal' },
  InternalDetailsNext: {
    name: '内部详情内容',
    category: 'disposal',
    parent: 'InternalDetails',
    parentRoute: '/web/internal-details',
  },
  ZmnDetails: {
    name: '啄木鸟工单详情',
    category: 'disposal',
    parent: 'InternalDetailsNext',
    parentRoute: '/web/internal-details-next',
  },
  ComplaintDetailsZmn: {
    name: '啄木鸟投诉详情',
    category: 'disposal',
    parent: 'ZmnDetails',
    parentRoute: '/web/zmn-details',
  },
  ComplaintContentZmn: {
    name: '啄木鸟投诉内容',
    category: 'disposal',
    parent: 'ComplaintDetailsZmn',
    parentRoute: '/web/complaint-details-zmn',
  },
  YeyxDetails: {
    name: '言而有信工单详情',
    category: 'disposal',
    parent: 'InternalDetailsNext',
    parentRoute: '/web/internal-details-next',
  },
  ComplaintDetailsYeyx: {
    name: '言而有信投诉详情',
    category: 'disposal',
    parent: 'YeyxDetails',
    parentRoute: '/web/yeyx-details',
  },
  ComplaintContentYeyx: {
    name: '言而有信投诉内容',
    category: 'disposal',
    parent: 'ComplaintDetailsYeyx',
    parentRoute: '/web/complaint-details-yeyx',
  },

  PersonalInformation: { name: '个人信息', category: 'user' },
  AuthorityManagement: { name: '权限管理', category: 'user' },
  CompetentAuthority: { name: '监管部门', category: 'media' },
  ServiceProvider: { name: '合作服务商', category: 'media' },
  MediaLibrary: { name: '媒体管理', category: 'media' },
  MediaReport: { name: '媒体报道', category: 'media' },
  MediaResourceDashboard: { name: '媒体资源看板', category: 'media' },
  MediaDetails: {
    name: '媒体详情',
    category: 'media',
    parent: 'MediaLibrary',
    parentRoute: '/web/media-library',
  },

  AccountabilityDashboard: { name: '追责看板', category: 'accountability' },
  AccountabilityList: { name: '追责列表', category: 'accountability' },
  Rectification: { name: '整改任务', category: 'accountability' },
  TaskTrack: {
    name: '任务追踪',
    category: 'accountability',
    parent: 'Rectification',
    parentRoute: '/web/rectification',
  },
  RectificationDetails: {
    name: '追责详情', // 你的页面标题，完全不变
    category: 'accountability', // 对应【追责中心】，固定值
    parent: 'AccountabilityList', // 父级是【追责列表】，路由name和上面一致
    parentRoute: '/web/accountablity-list', // 父级路由地址，你的拼写【少c】，完全保留！
  },
  // 新增：成本预算库-支出列表
  ExpenseList: {
    name: '成本支出列表',
    category: 'budget' ,
    parentRoute: '/web/expense-list'
  },
  CostBudgetDashboard: { name: '成本看板', category: 'budget' },
  expenseDetails: {
    name: '成本详情',
    category: 'budget',
    parent: 'ExpenseList',
    parentRoute: '/web/expense-list',
  }
}

// 分类基础路径
const categoryBases = {
  monitor: { text: '监测中心' },
  disposal: { text: '任务中心' },
  user: { text: '用户管理' },
  media: { text: '媒体资源库' },
  accountability: { text: '追责中心' },
  budget: { text: '成本预算库' },
}

const breadcrumbs: ComputedRef<BreadcrumbItem[]> = computed(() => {
  console.log('当前路由名称是:', route.name) // 👈 加这行看控制台输出了什么
  const routeName = route.name as string
  const config = pageConfigs[routeName]

  if (!config) {
    return [{ text: '司南系统' }]
  }

  const result: BreadcrumbItem[] = [categoryBases[config.category]]

  // 支持多级父级链
  const chain: BreadcrumbItem[] = []
  const visited = new Set<string>()

  let current = config
  while (current.parent && current.parentRoute) {
    if (visited.has(current.parent)) {
      break
    }
    visited.add(current.parent)

    const parentConfig = pageConfigs[current.parent]
    if (!parentConfig) {
      break
    }

    // 工单详情页返回“内部详情内容”时需带 id 和 query，避免白屏
    let toUrl = current.parentRoute
    if (routeName === 'AccountPostList' && current.parent === 'PublicopinionContent') {
      const queryParent = route.query.parentRoute
      const uniqueId = String(route.params.uniqueId ?? '')
      toUrl = typeof queryParent === 'string' && queryParent.trim()
        ? queryParent.trim()
        : `/web/public-opinion-content/${encodeURIComponent(uniqueId)}`
    }
    if (
      (routeName === 'ZmnDetails' || routeName === 'YeyxDetails') &&
      current.parent === 'InternalDetailsNext' &&
      typeof route.query.parentRoute === 'string' &&
      route.query.parentRoute.trim()
    ) {
      toUrl = route.query.parentRoute.trim()
    }

    chain.push({ text: parentConfig.name, to: toUrl })
    current = parentConfig
  }

  chain.reverse()
  result.push(...chain)

  // 添加当前页面
  if (routeName === 'InternalDetailsNext') {
    const idRaw = route.query.id
    let suffix = ''
    if (idRaw) {
      if (Array.isArray(idRaw)) {
        suffix = idRaw.length > 1 ? `${idRaw[0]}等` : String(idRaw[0])
      } else {
        suffix = String(idRaw)
      }
    }
    result.push({ text: suffix ? `${config.name}：${suffix}` : config.name })
  } else if (routeName === 'AccountPostList') {
    result.push({ text: getAccountHistoryTypeLabel(String(route.params.type ?? '')) ?? config.name })
  } else {
    result.push({ text: config.name })
  }

  return result
})

type FeedbackDialogExpose = {
  open: () => void
}

const feedbackDialogRef = ref<FeedbackDialogExpose | null>(null)

// 节日提醒：展示最近一个节日
// 自然月窗口：本月 n 日 → 下月 n 日（下月无 n 日则取最后一天）
function daysInNaturalMonth(today: Date): number {
  const d = today.getDate()
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)
  const lastDay = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).getDate()
  const end = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), Math.min(d, lastDay))
  return Math.ceil((end.getTime() - today.getTime()) / 86400000)
}
const holidayTips = ref(getUpcomingHolidays(new Date(), daysInNaturalMonth(new Date())))

const openFeedback = () => {
  feedbackDialogRef.value?.open()
  toggleImag.value = true
  currentIcon.value = image2
}

// 重置图标到初始状态的方法
const resetFeedbackIcon = () => {
  toggleImag.value = false
  currentIcon.value = image1
}
//问题反馈图标
const currentIcon = ref(image1)
const toggleImag = ref(false)

//反馈自动定位
// 新增：获取当前页面名称
const currentPageName = computed(() => {
  const routeName = route.name as string
  const config = pageConfigs[routeName]
  return config ? config.name : '' // 从页面配置中获取当前页面名称
})


const router = useRouter()

const normalizeQueryRoute = (v: unknown): string | null => {
  if (typeof v === 'string' && v.trim()) return v
  if (Array.isArray(v) && typeof v[0] === 'string' && v[0].trim()) return v[0]
  return null
}

const FALLBACK_PATH = '/web/home'

const backToParent = async () => {
  const queryParent = normalizeQueryRoute(route.query.parentRoute)
  if (queryParent) {
    await router.push(queryParent)
    return
  }

  // 使用面包屑中最后一个可点击的 to 作为“父级/上一层”
  // breadcrumbs 是 ComputedRef<BreadcrumbItem\[]>
  const lastNavigable = [...breadcrumbs.value].reverse().find((c) => typeof c.to === 'string' && c.to)
  if (lastNavigable?.to) {
    await router.push(lastNavigable.to)
    return
  }

  ElMessage.warning('无上级页面可返回')

  // 回退到默认路径
  // await router.push(FALLBACK_PATH)
}
</script>

<template>
  <div class="breadcrumb-row">
    <div class="title-container">
      <span class="back-btn" @click="backToParent">
        <svg
          class="back-icon"
          width="24"
          height="24"
          viewBox="0 0 1024 1024"
          style="margin-right: 8px; display: block"
        >
          <path d="M755.498667 225.834667a42.666667 42.666667 0 0 1 0 60.330666L529.664 512l225.834667 225.834667a42.666667 42.666667 0 1 1-60.330667 60.330666l-256-256a42.666667 42.666667 0 0 1 0-60.330666l256-256a42.666667 42.666667 0 0 1 60.330667 0z m-256 0a42.666667 42.666667 0 0 1 0 60.330666L273.664 512l225.834667 225.834667a42.666667 42.666667 0 1 1-60.330667 60.330666l-256-256a42.666667 42.666667 0 0 1 0-60.330666l256-256a42.666667 42.666667 0 0 1 60.330667 0z"
                fill="currentColor"
          />
        </svg>
      </span>
      <template v-for="(crumb, index) in breadcrumbs" :key="index">
        <template v-if="index < breadcrumbs.length - 1">
          <router-link v-if="crumb.to" :to="crumb.to" class="breadcrumb-path">{{
            crumb.text
          }}</router-link>
          <span v-else class="breadcrumb-path">{{ crumb.text }}</span>
          <span class="breadcrumb-separator">/</span>
        </template>
        <h1 v-else class="breadcrumb-current">{{ crumb.text }}</h1>
      </template>
      <!-- 仪表盘“自定义”下拉，紧跟在面包屑后面 -->
      <div class="breadcrumb-actions">
        <el-dropdown v-if="isDashboardPage" trigger="click">
          <el-button type="primary">
            自定义<el-icon class="el-icon--right">
              <ArrowDown />
            </el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item v-for="cardName in dashboardStore.cardOptions" :key="cardName">
                <el-checkbox
                  :model-value="dashboardStore.selectedCardsRef[cardName]"
                  :label="cardName"
                  @change="dashboardStore.toggleCardVisibility(cardName)"
                />
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
      <!-- 节日提醒：只显示最近一个 -->
      <div v-if="holidayTips && holidayTips.length" class="holiday-reminder">
        <el-tooltip
          v-for="(holiday, index) in holidayTips"
          :key="index"
          :content="
            holiday.daysLeft === 0
              ? `今天是${holiday.name}`
              : `距离${holiday.name}还有${holiday.daysLeft}天（${holiday.date.toLocaleDateString()}）`
          "
          placement="bottom"
        >
          <el-tag type="warning" size="small" style="margin-left: 6px">
            {{
              holiday.daysLeft === 0
                ? `今天是：${holiday.name}`
                : `节日预告：${holiday.name} · ${holiday.daysLeft}天`
            }}
          </el-tag>
        </el-tooltip>
      </div>
      <div v-if="isDashboardPage" class="api-ratio-stat">
        <span class="api-ratio-label">获取舆情总数 / API接口总量</span>
        <span class="api-ratio-value">
          {{ dashboardStore.formattedFetchedOpinionTotal }} /
          {{ dashboardStore.formattedApiTotal }}
        </span>
        <span class="api-ratio-percent">{{ dashboardStore.opinionApiRatio }}</span>
        <!-- A4/6.1：首次失败显示“暂不可用”；已有成功值保留并标明更新时间；真实成功 0 才显示 0 -->
        <span
          v-if="dashboardStore.fetchedOpinionTotalState === 'failed'"
          class="api-ratio-hint"
          :title="dashboardStore.fetchedOpinionTotalAt ? `上次成功：${new Date(dashboardStore.fetchedOpinionTotalAt).toLocaleString()}` : ''"
        >暂不可用</span>
      </div>
    </div>
    <!--    <FeedbackDialog ref="feedbackDialogRef" />-->
    <FeedbackDialog
      ref="feedbackDialogRef"
      @close="resetFeedbackIcon"
      :current-page="currentPageName"
    />
  </div>
</template>

<style scoped>
.breadcrumb-row {
  display: flex;
  align-items: center;
  height: 44px;
  background: #fff;
  padding: 0 24px;
  box-sizing: border-box;
  border-bottom: 1px solid #f0f0f0;
}

.title-container {
  display: flex;
  align-items: center;
  width: 100%;
}

.back-btn {
  color: #606266;
  cursor: pointer;
}

.back-btn:hover {
  color: #409eff; /* Element Plus 主色 */
}


.breadcrumb-actions {
  margin-left: 16px;
  /* 与面包屑间距 */
}

/* 新增：节日提醒样式 */
.holiday-reminder {
  margin-left: 12px;
}

.api-ratio-stat {
  display: flex;
  align-items: center;
  height: 32px;
  margin-left: auto;
  padding: 0 14px;
  flex-shrink: 0;
  gap: 10px;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  font-size: 13px;
  white-space: nowrap;
}

.api-ratio-label {
  color: #606266;
}

.api-ratio-value {
  color: #303133;
  font-weight: 600;
}

.api-ratio-percent {
  color: #409eff;
  font-weight: 700;
}

.api-ratio-hint {
  margin-left: 6px;
  font-size: 12px;
  color: #e6a23c;
  cursor: help;
}

.breadcrumb-path {
  font-size: 16px;
  color: #666;
  text-decoration: none;
}

.breadcrumb-path:hover {
  color: #409eff;
}

.breadcrumb-separator {
  margin: 0 8px;
  font-size: 16px;
  color: #666;
}

.breadcrumb-current {
  font-size: 20px;
  color: #333;
  margin: 0;
  display: inline;
}

.feedback-button {
  margin-left: auto;
  margin-right: 50px;
  /* margin-top: -5px; */
  /* border: 1px solid blue; */
}

.feedback-icon {
  cursor: pointer;
  width: 40px;
  height: 40px;
  /* margin-top: 10px; */
  /* border: 1px solid red; */
}
</style>
